import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { Container, Alert, Spinner } from 'react-bootstrap';

/**
 * 處理 Authentic OIDC 認證回調的頁面組件
 * 後端已經處理了 OAuth code exchange，這裡只需要檢查錯誤和獲取用戶信息
 */
const CallbackPage = () => {
  const { handleCallback, error, loading } = useAuth();
  const [localError, setLocalError] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const processedRef = useRef(false);

  useEffect(() => {
    // 檢查是否已經處理過，避免重複處理
    if (processedRef.current) {
      return;
    }

    // 標記為已處理
    processedRef.current = true;

    // 檢查是否有錯誤參數（由後端重導向時帶入）
    const errorParam = searchParams.get('error');

    if (errorParam) {
      let errorMessage = '登入失敗，請重試';

      switch (errorParam) {
        case 'state_mismatch':
          errorMessage = '登入驗證失敗（CSRF），請重新登入';
          break;
        case 'auth_failed':
          errorMessage = '認證失敗，請確認帳號權限';
          break;
        case 'access_denied':
          errorMessage = '您拒絕了授權請求';
          break;
        default:
          errorMessage = `登入錯誤：${errorParam}`;
      }

      setLocalError(errorMessage);

      // 2 秒後重導向回首頁
      setTimeout(() => {
        navigate('/');
      }, 2000);

      return;
    }

    // 處理成功的回調
    const processCallback = async () => {
      try {
        const success = await handleCallback();
        if (!success) {
          setLocalError('登入處理失敗');
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
        // 成功的話，handleCallback 會自動導航到適當的頁面
      } catch (err) {
        console.error('處理回調時發生錯誤:', err);
        setLocalError('處理認證時發生錯誤: ' + (err.message || '未知錯誤'));
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    };

    processCallback();
  }, [handleCallback, navigate, searchParams]);

  return (
    <Container className="mt-5 text-center">
      <h2>認證處理中</h2>

      {(loading || (!error && !localError)) && (
        <div className="my-4">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">正在處理您的登入請求，請稍候...</p>
          <p className="text-muted small">幾秒後將自動重定向</p>
        </div>
      )}

      {(error || localError) && (
        <Alert variant="danger" className="my-4">
          <Alert.Heading>登入處理出錯</Alert.Heading>
          <p>{error || localError}</p>
          <hr />
          <p className="mb-0">
            將在 2 秒後自動返回首頁，或點擊 <Alert.Link href="/">這裡</Alert.Link> 立即返回。
          </p>
        </Alert>
      )}
    </Container>
  );
};

export default CallbackPage;
